// Platform Admin — 🧹 School DB Cleanup Panel
// =============================================
// State + District canonicalization control center.
// Shows duplicate counts, planned migrations, and audit log with one-click apply.
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_BADGE = {
  no_change: 'bg-slate-100 text-slate-600',
  rename: 'bg-amber-100 text-amber-800',
  move_to_affiliation: 'bg-purple-100 text-purple-800',
  needs_review: 'bg-red-100 text-red-700',
};

const ACTION_LABEL_HI = {
  no_change: 'Same',
  rename: 'Merge',
  move_to_affiliation: '→ Affiliation',
  needs_review: '⚠ Review',
};

export default function SchoolDbCleanupPanel({ token, language = 'hi' }) {
  const [tab, setTab] = useState('states');
  const [statePreview, setStatePreview] = useState(null);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateApplyResult, setStateApplyResult] = useState(null);
  const [stateApplying, setStateApplying] = useState(false);

  const [districtSummary, setDistrictSummary] = useState(null);
  const [districtState, setDistrictState] = useState('');
  const [districtPreview, setDistrictPreview] = useState(null);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [districtApplying, setDistrictApplying] = useState(false);
  const [districtApplyResult, setDistrictApplyResult] = useState(null);

  const [reviewItems, setReviewItems] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewReason, setReviewReason] = useState('');

  const [audit, setAudit] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const loadStatePreview = async () => {
    setStateLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/school-finder/cleanup/state-preview`, { headers });
      const d = await r.json();
      if (d.success) setStatePreview(d);
    } catch (e) { console.warn('state preview:', e); }
    setStateLoading(false);
  };

  const applyStateCleanup = async (dryRun = false) => {
    setStateApplying(true);
    setStateApplyResult(null);
    try {
      const r = await fetch(`${API_URL}/api/school-finder/cleanup/states/apply?dry_run=${dryRun}`, {
        method: 'POST', headers,
      });
      const d = await r.json();
      setStateApplyResult(d);
      if (d.success && !dryRun) await loadStatePreview();
      await loadAudit();
    } catch (e) {
      setStateApplyResult({ success: false, detail: e.message });
    }
    setStateApplying(false);
  };

  const loadDistrictSummary = async () => {
    setDistrictLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/school-finder/cleanup/district-preview`, { headers });
      const d = await r.json();
      if (d.success) setDistrictSummary(d);
    } catch (e) { console.warn('district summary:', e); }
    setDistrictLoading(false);
  };

  const loadDistrictPreview = async (state) => {
    if (!state) return;
    setDistrictLoading(true);
    setDistrictPreview(null);
    try {
      const r = await fetch(`${API_URL}/api/school-finder/cleanup/district-preview?state=${encodeURIComponent(state)}&threshold=88`, { headers });
      const d = await r.json();
      if (d.success) setDistrictPreview(d);
    } catch (e) { console.warn('district preview:', e); }
    setDistrictLoading(false);
  };

  const autoApplyDistricts = async (threshold = 95, dryRun = false) => {
    setDistrictApplying(true);
    setDistrictApplyResult(null);
    try {
      const r = await fetch(`${API_URL}/api/school-finder/cleanup/districts/auto-apply?threshold=${threshold}&dry_run=${dryRun}`, {
        method: 'POST', headers,
      });
      const d = await r.json();
      setDistrictApplyResult(d);
      if (d.success && !dryRun) {
        await loadDistrictSummary();
        if (districtState) await loadDistrictPreview(districtState);
      }
      await loadAudit();
    } catch (e) {
      setDistrictApplyResult({ success: false, detail: e.message });
    }
    setDistrictApplying(false);
  };

  const loadReview = async (reason = '') => {
    setReviewLoading(true);
    try {
      const url = `${API_URL}/api/school-finder/cleanup/needs-review?limit=50${reason ? `&reason=${reason}` : ''}`;
      const r = await fetch(url, { headers });
      const d = await r.json();
      if (d.success) setReviewItems(d);
    } catch (e) { console.warn('review:', e); }
    setReviewLoading(false);
  };

  const loadAudit = async () => {
    try {
      const r = await fetch(`${API_URL}/api/school-finder/cleanup/audit?limit=20`, { headers });
      const d = await r.json();
      if (d.success) setAudit(d.items || []);
    } catch (e) { console.warn('audit:', e); }
  };

  useEffect(() => {
    loadStatePreview();
    loadAudit();
    /* eslint-disable-next-line */
  }, []);

  return (
    <div className="space-y-4" data-testid="school-db-cleanup-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-2xl p-5">
        <h2 className="text-xl font-black flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          🧹 {language === 'hi' ? 'School DB Cleanup' : 'School DB Cleanup'}
        </h2>
        <p className="text-sm text-white/90 mt-1">
          {language === 'hi'
            ? 'State/District canonicalization — duplicates merge करें, garbage entries flag करें।'
            : 'State/District canonicalization — merge duplicates, flag garbage entries.'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b" data-testid="cleanup-tabs">
        {[
          { id: 'states', label: language === 'hi' ? '🗺 States' : '🗺 States' },
          { id: 'districts', label: language === 'hi' ? '🏘 Districts' : '🏘 Districts' },
          { id: 'review', label: language === 'hi' ? '⚠ Needs Review' : '⚠ Needs Review' },
          { id: 'audit', label: language === 'hi' ? '📜 Audit Log' : '📜 Audit Log' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id);
              if (t.id === 'review' && !reviewItems) loadReview();
              if (t.id === 'districts' && !districtSummary) loadDistrictSummary();
              if (t.id === 'audit') loadAudit();
            }}
            className={`px-3 py-2 text-sm font-semibold border-b-2 transition ${tab === t.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            data-testid={`cleanup-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* States Tab */}
      {tab === 'states' && (
        <div className="space-y-3" data-testid="states-tab-content">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>
                {language === 'hi' ? 'State Cleanup Preview' : 'State Cleanup Preview'}
              </h3>
              <p className="text-xs text-slate-500">
                {language === 'hi' ? 'Duplicate spellings → canonical state names में merge होंगे।' : 'Duplicate spellings will merge into canonical names.'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => applyStateCleanup(true)}
                disabled={stateApplying}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-sm font-semibold rounded-lg disabled:opacity-50"
                data-testid="states-dry-run-btn"
              >🔍 Dry Run</button>
              <button
                onClick={() => applyStateCleanup(false)}
                disabled={stateApplying}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg disabled:opacity-50"
                data-testid="states-apply-btn"
              >{stateApplying ? '…' : (language === 'hi' ? '🚀 Apply Cleanup' : '🚀 Apply Cleanup')}</button>
            </div>
          </div>

          {stateLoading && <p className="text-sm text-slate-500">Loading…</p>}

          {statePreview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="states-summary">
                <SummaryTile label={language === 'hi' ? 'Distinct values' : 'Distinct values'} value={statePreview.total_distinct_state_values} color="slate" />
                <SummaryTile label={language === 'hi' ? 'Will merge' : 'Will merge'} value={statePreview.summary?.will_rename_or_merge} color="amber" />
                <SummaryTile label={language === 'hi' ? 'No change' : 'No change'} value={statePreview.summary?.no_change} color="emerald" />
                <SummaryTile label={language === 'hi' ? 'Needs review' : 'Needs review'} value={statePreview.summary?.needs_review} color="red" />
                <SummaryTile label={language === 'hi' ? 'Affiliations' : 'Affiliations'} value={statePreview.summary?.affiliations_count} color="purple" />
              </div>

              {stateApplyResult && (
                <div className={`text-sm px-3 py-2 rounded ${stateApplyResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="states-apply-result">
                  {stateApplyResult.success
                    ? `${stateApplyResult.dry_run ? '[Dry Run] ' : '✅ '}${stateApplyResult.renamed} renamed · ${stateApplyResult.moved_to_affiliation} → affiliation · ${stateApplyResult.flagged_review} flagged for review`
                    : `❌ ${stateApplyResult.detail}`}
                </div>
              )}

              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Raw State</th>
                      <th className="px-3 py-2 text-right">Count</th>
                      <th className="px-3 py-2 text-left">Action</th>
                      <th className="px-3 py-2 text-left">→ Canonical / Affiliation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" data-testid="states-table-body">
                    {(statePreview.rows || []).map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50" data-testid={`state-row-${i}`}>
                        <td className="px-3 py-2 font-mono">{row.raw_state || <span className="text-slate-400 italic">null</span>}</td>
                        <td className="px-3 py-2 text-right font-bold">{row.count.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ACTION_BADGE[row.action] || 'bg-slate-100'}`}>
                            {ACTION_LABEL_HI[row.action] || row.action}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {row.canonical_state || (row.affiliation && <span className="text-purple-700 font-bold">{row.affiliation}</span>) || <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Districts Tab */}
      {tab === 'districts' && (
        <div className="space-y-3" data-testid="districts-tab-content">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>
                {language === 'hi' ? 'District Cleanup' : 'District Cleanup'}
              </h3>
              <p className="text-xs text-slate-500">
                {language === 'hi'
                  ? 'Threshold ≥95 = case-only & spelling rules auto-apply। कम threshold पर manual review चाहिए।'
                  : 'Threshold ≥95 auto-applies safe case/spelling fixes. Lower thresholds need review.'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => autoApplyDistricts(95, true)}
                disabled={districtApplying}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-sm font-semibold rounded-lg disabled:opacity-50"
                data-testid="districts-dry-run-btn"
              >🔍 Dry Run @ 95</button>
              <button
                onClick={() => autoApplyDistricts(95, false)}
                disabled={districtApplying}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg disabled:opacity-50"
                data-testid="districts-apply-btn"
              >{districtApplying ? '…' : '🚀 Auto-Apply @ 95'}</button>
            </div>
          </div>

          {districtApplyResult && (
            <div className={`text-sm px-3 py-2 rounded ${districtApplyResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="districts-apply-result">
              {districtApplyResult.success
                ? `${districtApplyResult.dry_run ? '[Dry Run] ' : '✅ '}Rules: ${districtApplyResult.rule_based_renamed} · Fuzzy: ${districtApplyResult.fuzzy_renamed} · Garbage: ${districtApplyResult.flagged_garbage}`
                : `❌ ${districtApplyResult.detail}`}
            </div>
          )}

          {/* Cross-state summary */}
          {districtSummary && districtSummary.summary && (
            <div className="bg-white rounded-xl border p-3 max-h-64 overflow-y-auto" data-testid="districts-summary">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'hi' ? 'State-wise District Counts' : 'State-wise District Counts'}</p>
              <table className="w-full text-xs">
                <thead className="text-[10px] text-slate-500 uppercase">
                  <tr>
                    <th className="text-left">State</th>
                    <th className="text-right">Districts</th>
                    <th className="text-right">Schools</th>
                    <th className="text-right">No district</th>
                    <th className="text-right">Garbage</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {districtSummary.summary.map((s) => (
                    <tr key={s.state} className="hover:bg-slate-50" data-testid={`district-state-row-${s.state}`}>
                      <td className="py-1.5 font-medium">{s.state}</td>
                      <td className="py-1.5 text-right">{s.distinct_districts}</td>
                      <td className="py-1.5 text-right">{s.total_schools.toLocaleString('en-IN')}</td>
                      <td className="py-1.5 text-right text-slate-500">{s.null_count > 0 ? s.null_count.toLocaleString('en-IN') : '—'}</td>
                      <td className="py-1.5 text-right text-orange-600">{s.garbage_count > 0 ? s.garbage_count : '—'}</td>
                      <td className="py-1.5 text-right">
                        <button
                          onClick={() => { setDistrictState(s.state); loadDistrictPreview(s.state); }}
                          className="text-[10px] px-2 py-0.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded"
                          data-testid={`load-state-${s.state}`}
                        >🔍</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Per-state cluster preview */}
          {districtPreview && (
            <div className="bg-white rounded-xl border p-3" data-testid="district-preview-detail">
              <p className="text-sm font-bold mb-2">📍 {districtPreview.state} — {districtPreview.total_distinct_districts} {language === 'hi' ? 'districts' : 'districts'}</p>
              {districtPreview.clusters && districtPreview.clusters.length > 0 ? (
                <div className="space-y-1 mb-3">
                  <p className="text-[10px] font-semibold text-amber-700 uppercase">{language === 'hi' ? `Fuzzy Duplicate Clusters (${districtPreview.clusters.length})` : `Fuzzy Duplicate Clusters (${districtPreview.clusters.length})`}</p>
                  {districtPreview.clusters.map((c, i) => (
                    <div key={i} className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      <strong>{c.canonical}</strong> ({c.canonical_count}) ← {c.members.map((m) => `${m.name}(${m.count})`).join(', ')}
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-slate-500 mb-2">{language === 'hi' ? 'कोई fuzzy cluster नहीं मिला।' : 'No fuzzy clusters at this threshold.'}</p>}
              {districtPreview.garbage && districtPreview.garbage.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-red-700 uppercase">{language === 'hi' ? `Garbage Entries (${districtPreview.garbage.length})` : `Garbage Entries (${districtPreview.garbage.length})`}</p>
                  <div className="space-y-1">
                    {districtPreview.garbage.map((g, i) => (
                      <div key={i} className="text-xs bg-red-50 border border-red-200 rounded px-2 py-1">
                        <code>{g.raw === null ? 'null' : g.raw}</code> · {g.count} schools · <span className="text-red-700">{g.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Needs Review Tab */}
      {tab === 'review' && (
        <div className="space-y-3" data-testid="review-tab-content">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>
              {language === 'hi' ? '⚠ Manual Review Queue' : '⚠ Manual Review Queue'}
            </h3>
            <div className="flex gap-2">
              <select value={reviewReason} onChange={(e) => { setReviewReason(e.target.value); loadReview(e.target.value); }} className="text-xs px-2 py-1 border rounded" data-testid="review-reason-filter">
                <option value="">All reasons</option>
                <option value="state_unknown">state_unknown</option>
                <option value="district_garbage">district_garbage</option>
              </select>
              <button onClick={() => loadReview(reviewReason)} disabled={reviewLoading} className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-xs rounded">↻ Refresh</button>
            </div>
          </div>

          {reviewLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {reviewItems && (
            <>
              <p className="text-xs text-slate-500" data-testid="review-total">
                {language === 'hi' ? `कुल ${reviewItems.total.toLocaleString('en-IN')} schools review चाहिए (showing ${reviewItems.items.length})` : `${reviewItems.total.toLocaleString('en-IN')} schools need review (showing ${reviewItems.items.length})`}
              </p>
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase">
                    <tr>
                      <th className="px-2 py-1 text-left">School</th>
                      <th className="px-2 py-1">UDISE</th>
                      <th className="px-2 py-1">State</th>
                      <th className="px-2 py-1">District</th>
                      <th className="px-2 py-1">Affiliation</th>
                      <th className="px-2 py-1">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {reviewItems.items.map((it, i) => (
                      <tr key={it.id || i} data-testid={`review-row-${i}`}>
                        <td className="px-2 py-1 font-medium truncate max-w-xs">{it.school_name || '—'}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">{it.udise_code || '—'}</td>
                        <td className="px-2 py-1">{it.state || <span className="text-slate-400">null</span>}</td>
                        <td className="px-2 py-1">{it.district || <span className="text-slate-400">null</span>}</td>
                        <td className="px-2 py-1">{it.affiliation || '—'}</td>
                        <td className="px-2 py-1 text-red-600 text-[10px]">{it.needs_review_reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Audit Tab */}
      {tab === 'audit' && (
        <div className="space-y-2" data-testid="audit-tab-content">
          <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>
            {language === 'hi' ? '📜 Cleanup Audit Log' : '📜 Cleanup Audit Log'}
          </h3>
          {audit.length === 0 ? (
            <p className="text-xs text-slate-500">{language === 'hi' ? 'अभी कोई cleanup नहीं हुआ।' : 'No cleanup runs yet.'}</p>
          ) : (
            <div className="bg-white rounded-xl border divide-y">
              {audit.map((a, i) => (
                <div key={i} className="p-3 text-xs" data-testid={`audit-row-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{a.type}</span>
                      {a.dry_run && <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 rounded">DRY RUN</span>}
                    </div>
                    <span className="text-[10px] text-slate-500">{new Date(a.finished_at).toLocaleString()}</span>
                  </div>
                  <p className="text-slate-600 mt-1">
                    {a.renamed != null && <>renamed=<strong>{a.renamed}</strong> · </>}
                    {a.rule_based_renamed != null && <>rules=<strong>{a.rule_based_renamed}</strong> · </>}
                    {a.fuzzy_renamed != null && <>fuzzy=<strong>{a.fuzzy_renamed}</strong> · </>}
                    {a.moved_to_affiliation != null && <>→ affiliation=<strong>{a.moved_to_affiliation}</strong> · </>}
                    {a.flagged_review != null && <>flagged_review=<strong>{a.flagged_review}</strong> · </>}
                    {a.flagged_garbage != null && <>flagged_garbage=<strong>{a.flagged_garbage}</strong> · </>}
                    {a.threshold && <>threshold=<strong>{a.threshold}</strong> · </>}
                    by <strong>{a.by}</strong>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, color = 'slate' }) {
  const colorMap = {
    slate: 'from-slate-500 to-slate-600',
    amber: 'from-amber-500 to-orange-600',
    emerald: 'from-emerald-500 to-teal-600',
    red: 'from-rose-500 to-red-600',
    purple: 'from-purple-500 to-fuchsia-600',
  };
  return (
    <div className={`bg-gradient-to-br ${colorMap[color] || colorMap.slate} text-white rounded-xl p-3`}>
      <p className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">{label}</p>
      <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Outfit' }}>{(value ?? 0).toLocaleString('en-IN')}</p>
    </div>
  );
}
